import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../auth/context';
import { BrandMark } from '../components/BrandMark';
import { dispatchForgotPassword } from '../auth/dispatchAuth';
import { safeReturnPath } from '../auth/handoff';
import { ErrorNote, Note } from '../components/ErrorNote';
import { Field } from '../components/Field';
import { Spinner } from '../components/Spinner';
import './SignIn.css';

/**
 * Dashboard-credential sign-in: tenant key + admin email + password, exchanged
 * at dispatch-be for a Firebase custom token. The tenant the CRM trusts is the
 * realm claim on that token, not what was typed here.
 */
export function SignIn() {
  const { status, lastTenant, signIn } = useAuth();
  const location = useLocation();
  const [tenant, setTenant] = useState(lastTenant);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Where RequireAuth bounced them from, so a deep link survives the detour.
  const from = safeReturnPath((location.state as { from?: string } | null)?.from ?? null);
  if (status === 'signedIn') return <Navigate to={from} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setForgotSent(false);
    if (!tenant.trim() || !email.trim() || (!forgotMode && !password)) {
      setError('Fill in every field.');
      return;
    }
    setBusy(true);
    try {
      if (forgotMode) {
        await dispatchForgotPassword(tenant.trim(), email);
        setForgotSent(true);
      } else {
        await signIn(tenant, email, password);
        // RequireAuth redirects once the auth state flips.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin-wrap">
      <form className="card signin-card" onSubmit={submit}>
        <div className="signin-brand">
          <BrandMark className="wordmark-badge signin-mark" />
          <div>
            <h1 className="signin-title">LimoSphere CRM</h1>
            <p className="signin-sub">
              {forgotMode
                ? 'We’ll email you a link to reset your dashboard password.'
                : 'Sign in with your dispatch dashboard admin account.'}
            </p>
          </div>
        </div>

        {error && <ErrorNote error={error} />}
        {forgotSent && (
          <Note tone="ok">If that account exists, a reset email is on its way.</Note>
        )}

        <Field label="Tenant key" hint="The company key you use on the dashboard.">
          <input
            className="input"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            autoComplete="organization"
            autoFocus={!lastTenant}
            disabled={busy}
          />
        </Field>
        <Field label="Email">
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus={!!lastTenant}
            disabled={busy}
          />
        </Field>
        {!forgotMode && (
          <Field label="Password">
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={busy}
            />
          </Field>
        )}

        <button className="btn btn-primary signin-submit" type="submit" disabled={busy}>
          {busy && <Spinner />}
          {forgotMode ? 'Send reset link' : 'Sign in'}
        </button>

        <button
          type="button"
          className="btn btn-ghost signin-alt"
          disabled={busy}
          onClick={() => {
            setForgotMode(!forgotMode);
            setError(null);
            setForgotSent(false);
          }}
        >
          {forgotMode ? 'Back to sign in' : 'Forgot password?'}
        </button>
      </form>
    </div>
  );
}
